import type { RealCall } from '../types.js';
import { maskAni, formatDuration, formatCallTime } from '../types.js';

interface CallCardProps {
  call: RealCall;
  onDetail: (callId: string) => void;
}

const tierConfig: Record<string, { label: string; className: string }> = {
  tier1: { label: 'Tier 1', className: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' },
  tier2: { label: 'Tier 2', className: 'bg-blue-500/15 text-blue-400 border border-blue-500/30' },
  tier3: { label: 'Tier 3', className: 'bg-amber-500/15 text-amber-400 border border-amber-500/30' },
  unqualified: { label: 'Unqualified', className: 'bg-rose-500/15 text-rose-400 border border-rose-500/30' },
};

const intentConfig: Record<string, { label: string; className: string }> = {
  sales: { label: 'Sales', className: 'bg-blue-600/20 text-blue-300 border border-blue-600/30' },
  service: { label: 'Service', className: 'bg-slate-600/30 text-slate-300 border border-slate-600/40' },
  ivr_only: { label: 'IVR Only', className: 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/30' },
  unknown: { label: 'Unknown', className: 'bg-slate-700/30 text-slate-400 border border-slate-600/30' },
};

const defaultTierStyle = 'bg-slate-700/30 text-slate-400 border border-slate-600/30';
const defaultIntentStyle = 'bg-slate-700/30 text-slate-400 border border-slate-600/30';

export function CallCard({ call, onDetail }: CallCardProps) {
  const tier = call.classification?.tier;
  const intent = call.classification?.primaryIntent;
  const tierCfg = tierConfig[tier ?? ''] ?? { label: tier ?? 'Unknown', className: defaultTierStyle };
  const intentCfg = intentConfig[intent ?? ''] ?? { label: intent?.replace('_', ' ') ?? 'Unknown', className: defaultIntentStyle };
  const revenue = parseFloat(call.revenue ?? '0');
  const hasRecording = !!call.recordingUrl;

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 hover:border-slate-600 transition-colors">
      <div className="flex items-start gap-3">
        {/* Phone icon */}
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-800">
          <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm font-medium text-slate-200">{maskAni(call.callerAni)}</span>
              {call.callerState && (
                <>
                  <span className="text-slate-500 text-xs">·</span>
                  <span className="text-xs text-slate-400">{call.callerState}</span>
                </>
              )}
              {call.trafficSource && (
                <>
                  <span className="text-slate-500 text-xs">·</span>
                  <span className="text-xs text-blue-400 capitalize">{call.trafficSource}</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              {tier && <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${tierCfg.className}`}>{tierCfg.label}</span>}
              {intent && <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${intentCfg.className}`}>{intentCfg.label}</span>}
            </div>
          </div>

          {/* Meta row */}
          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
            <span className="text-xs text-slate-400">{formatCallTime(call.startedAt)}</span>
            <span className="text-xs text-slate-600">·</span>
            <span className="text-xs text-slate-400">{formatDuration(call.totalDurationSeconds)}</span>
            <span className="text-xs text-slate-600">·</span>
            <span className={`text-xs font-medium ${revenue > 0 ? 'text-emerald-400' : 'text-slate-500'}`}>
              {revenue > 0 ? `$${revenue.toFixed(2)}` : '$0.00'}
            </span>
            {call.classification?.disposition && (
              <>
                <span className="text-xs text-slate-600">·</span>
                <span className="text-xs text-slate-400 capitalize">{call.classification.disposition.replace('_', ' ')}</span>
              </>
            )}
          </div>

          {/* AI Summary */}
          {call.classification?.summary && (
            <p className="text-xs text-slate-400 mt-2 leading-relaxed line-clamp-2">{call.classification.summary}</p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-800">
        <button
          disabled={!hasRecording}
          title={hasRecording ? 'Play recording' : 'No recording'}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
          </svg>
          Play
        </button>

        <button
          onClick={() => onDetail(call.id)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 hover:text-blue-300 border border-blue-600/30 transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          Detail
        </button>

        <div className="ml-auto text-xs text-slate-600 font-mono">{call.id.slice(0, 8)}</div>
      </div>
    </div>
  );
}
